import { useCallback, useRef, useState } from "react";
import type { CanvasGraph, GraphCommand } from "./canvas-commands";
import { beginSession, closeHistoryAuthority, createHistory, executeCommand, finishSession, hasSession,
  redoHistory, resetHistory, undoHistory, updateView, type CanvasHistory, type EditToken } from "./canvas-history";

export function useCanvasHistory(initial?: CanvasGraph) {
  const [state, setState] = useState(() => createHistory(initial));
  const current = useRef(state);
  // All transitions run once at dispatch, outside React's replayable updaters.
  const transition = useCallback((action: (state: CanvasHistory) => CanvasHistory) => {
    const next = action(current.current);
    current.current = next;
    setState(next);
    return next;
  }, []);
  const execute = useCallback((command: GraphCommand, token?: EditToken) => transition(state => executeCommand(state, command, token)), [transition]);
  const begin = useCallback((kind: string) => transition(state => beginSession(state, kind)).session!.token, [transition]);
  const finish = useCallback((token: EditToken) => transition(state => finishSession(state, token)), [transition]);
  const view = useCallback((command: GraphCommand) => transition(state => updateView(state, command)), [transition]);
  const replay = useCallback((action: typeof undoHistory) => {
    const previous = current.current;
    const next = transition(action);
    // A refused replay alone advances the epoch while retaining the live graph.
    return { historyCleared: next.epoch !== previous.epoch && next.graph === previous.graph };
  }, [transition]);
  const undo = useCallback(() => replay(undoHistory), [replay]);
  const redo = useCallback(() => replay(redoHistory), [replay]);
  const reset = useCallback((graph: CanvasGraph) => transition(state => resetHistory(state, graph)), [transition]);
  const closeAuthority = useCallback(() => transition(closeHistoryAuthority), [transition]);
  const isSessionActive = useCallback((token: EditToken | null) => hasSession(current.current, token), []);
  return { state, current, execute, begin, finish, view, undo, redo, reset, closeAuthority, isSessionActive,
    canUndo: state.past.length > 0 || !!state.session?.changed,
    canRedo: state.future.length > 0 };
}
export type CanvasHistoryController = ReturnType<typeof useCanvasHistory>;
